import { once } from 'lodash';

import {
  EducationListItem,
  EducationListItemType,
  getDefaultEducationTitle,
  PAGE_KIND_TO_EDUCATION_SLIDE_ID,
  STATE_TAX_ITEMS,
} from '@/modules/education/Education.constants';
import { PresentationPageKind } from '@/types/schema';

export const EDUCATIONAL_PAGE_KINDS = [
  PresentationPageKind.WhyEstatePlanningMatters,
  PresentationPageKind.NoEstatePlanningConsequences,
  PresentationPageKind.EstateTaxesExplainer,
  PresentationPageKind.StateInheritanceTaxExplainer,
  PresentationPageKind.ManagingRecentInheritance,
  PresentationPageKind.PopularPlanningStrategies,
  PresentationPageKind.WhyUseTrust,
  PresentationPageKind.TrustBasics,
  PresentationPageKind.EstatePlanningGlossary,
  PresentationPageKind.SettingUpEstatePlan,
  PresentationPageKind.EstatePlanningChecklist,
  PresentationPageKind.GstExplainer,
  PresentationPageKind.CltStrategyExplainer,
  PresentationPageKind.CrtStrategyExplainer,
  PresentationPageKind.GratStrategyExplainer,
  PresentationPageKind.IlitStrategyExplainer,
  PresentationPageKind.InstallmentSaleStrategyExplainer,
  PresentationPageKind.PhilanthropicPlanningExplainer,
  PresentationPageKind.SlatStrategyExplainer,
  PresentationPageKind.QprtStrategyExplainer,
  PresentationPageKind.IdgtStrategyExplainer,
  PresentationPageKind.FlpStrategyExplainer,
  ...STATE_TAX_ITEMS,
] as const;

export type EducationalPageKind = (typeof EDUCATIONAL_PAGE_KINDS)[number];

function mapEducationalPageDisplayNames(
  getTitle: (slideId: EducationListItemType) => string
): Record<EducationalPageKind, string> {
  return Object.fromEntries(
    EDUCATIONAL_PAGE_KINDS.map((pageKind) => {
      const slideId = PAGE_KIND_TO_EDUCATION_SLIDE_ID[pageKind];
      if (!slideId) {
        throw new Error(`Missing education slide ID for ${pageKind}`);
      }
      return [pageKind, getTitle(slideId)];
    })
  ) as Record<EducationalPageKind, string>;
}

/**
 * Creates a mapping of educational page kinds to their display names.
 * @param educationMap - The education map with titles (from useEducationMap hook)
 * @returns A record mapping each educational page kind to its display name
 */
export function getEducationalPageToDisplayName(
  educationMap: Record<EducationListItemType, EducationListItem>
): Record<EducationalPageKind, string> {
  return mapEducationalPageDisplayNames(
    (slideId) => educationMap[slideId].title
  );
}

/**
 * Default mapping using default titles (without tenant overrides).
 * Lazily evaluated to avoid circular dependency issues at module load time.
 * Use `useEducationalPageDisplayNames()` hook in components to get overridden names.
 */
export const getEducationalPageDefaultDisplayNames = once(() =>
  mapEducationalPageDisplayNames(
    (slideId) => getDefaultEducationTitle(slideId) ?? ''
  )
);
